import React, { useState, useEffect } from 'react';
import { useNavigate } from '../utils/SimpleRouter';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [serviceAnnouncement, setServiceAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    // Detect browser language and set it if user hasn't manually set one
    const detectAndSetLanguage = () => {
      const storedLang = localStorage.getItem('i18nextLng');
      
      // If no language stored, detect from browser
      if (!storedLang) {
        const browserLang = navigator.language || navigator.userLanguage;
        const langCode = browserLang.split('-')[0]; // Get 'en' from 'en-US'
        
        // Map common language codes to our supported languages
        const supportedLanguages = ['no', 'en', 'sv', 'fi', 'da', 'de', 'it', 'es', 'pl'];
        const languageMap = {
          'nb': 'no', // Norwegian Bokmål
          'nn': 'no', // Norwegian Nynorsk
          'sv': 'sv', // Swedish
          'fi': 'fi', // Finnish
          'da': 'da', // Danish
          'de': 'de', // German
          'it': 'it', // Italian
          'es': 'es', // Spanish
          'pl': 'pl', // Polish
          'en': 'en'  // English
        };
        
        const detectedLang = languageMap[langCode] || (supportedLanguages.includes(langCode) ? langCode : 'en');
        
        console.log(`[LOGIN] Browser language detected: ${browserLang}, using: ${detectedLang}`);
        i18n.changeLanguage(detectedLang);
        localStorage.setItem('i18nextLng', detectedLang);
      }
    };
    
    detectAndSetLanguage();
  }, [i18n]);

  useEffect(() => {
    fetchServiceAnnouncement();
    
    // Check for saved credentials
    const savedEmail = localStorage.getItem('savedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  const fetchServiceAnnouncement = async () => {
    try {
      const response = await axios.get(`${API}/service-announcements`);
      const now = new Date();
      const active = response.data.find(ann => {
        const start = new Date(ann.start_date);
        const end = new Date(ann.end_date);
        return ann.is_active && now >= start && now <= end;
      });
      setServiceAnnouncement(active);
    } catch (error) {
      console.error('Failed to fetch service announcement:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Save both access token and refresh token
      localStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refreshToken', response.data.refresh_token);
      }
      
      // Save email if remember me is checked
      if (rememberMe) {
        localStorage.setItem('savedEmail', email);
      } else {
        localStorage.removeItem('savedEmail');
      }
      
      // Get user info
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });
      
      const user = userResponse.data;
      localStorage.setItem('user', JSON.stringify(user));
      
      // Set user's preferred language if specified (only if explicitly set, not null/empty)
      if (response.data.preferred_language && response.data.preferred_language.trim() !== '') {
        const preferredLang = response.data.preferred_language.trim();
        console.log('[LOGIN] Setting user preferred language:', preferredLang);
        localStorage.setItem('i18nextLng', preferredLang);
        // Set flag to prevent CustomerLayout from overriding with org default
        sessionStorage.setItem('userLoginLanguageSet', 'true');
        // Actually change the language in i18n
        await i18n.changeLanguage(preferredLang);
      }
      
      toast.success(t('login.loginSuccess'));
      
      if (user.role === 'super_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Feil brukernavn eller passord');
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, null, {
        params: { email: resetEmail }
      });
      
      if (response.data.success) {
        toast.success(t('login.resetEmailSent'));
        setShowForgotPassword(false);
        setResetEmail('');
      } else {
        toast.error(response.data.message || t('login.resetEmailFailed'));
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(t('login.genericError'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img 
            src="/infovegg-logo.svg" 
            alt="Infovegg.no" 
            style={{ height: '60px', width: 'auto' }}
          />
        </div>
        <p className="subtitle">{t('login.subtitle')}</p>
        
        <form onSubmit={handleLogin} data-testid="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('login.email')}</label>
            <input
              type="email"
              id="email"
              data-testid="email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('login.password')}</label>
            <input
              type="password"
              id="password"
              data-testid="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              fontWeight: 'normal',
              cursor: 'pointer',
              color: '#666'
            }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              {t('login.rememberMe')}
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            data-testid="login-button"
            disabled={loading}
          >
            {loading ? t('login.loggingIn') : t('login.loginButton')}
          </button>

          <div style={{ 
            textAlign: 'center', 
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e0e0e0'
          }}>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{ 
                color: '#50E3C2',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                textDecoration: 'none'
              }}
            >
              {t('login.forgotPassword')}
            </button>
          </div>
        </form>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('login.forgotPasswordTitle')}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              {t('login.forgotPasswordDescription')}
            </p>
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="reset-email">{t('login.email')}</label>
                <input
                  type="email"
                  id="reset-email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  {t('login.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={resetLoading}
                  style={{ flex: 1 }}
                >
                  {resetLoading ? t('login.sending') : t('login.sendLink')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {serviceAnnouncement && showAnnouncement && (
        <div className="service-announcement">
          <span>{serviceAnnouncement.message_text}</span>
          <button 
            className="close-announcement" 
            onClick={() => setShowAnnouncement(false)}
            aria-label={t('login.closeMessage')}
          >
            ✕
          </button>
        </div>
      )}
      
      <style>{`
        .login-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #50E3C2 0%, #429C88 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        
        .login-container {
          background: white;
          padding: 3rem;
          border-radius: 16px;
          max-width: 450px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        
        .subtitle {
          color: #666;
          margin-bottom: 2rem;
          text-align: center;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-group label {
          display: block;
          color: #2A2A2A;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        .form-group input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 1rem;
          color: #2A2A2A;
          background: white;
        }
        
        .form-group input:focus {
          outline: none;
          border-color: #50E3C2;
        }
        
        .btn {
          width: 100%;
          padding: 1rem;
          font-size: 1.1rem;
          margin-top: 1rem;
        }
        
        .service-announcement {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(26, 31, 46, 0.95);
          color: #ffffff;
          padding: 1rem 4rem 1rem 2rem;
          font-weight: 500;
          text-align: center;
          font-size: 0.95rem;
          box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
          border-top: 2px solid #50E3C2;
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .service-announcement span {
          flex: 1;
        }

        .close-announcement {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          transition: all 0.2s ease;
          font-weight: bold;
        }

        .close-announcement:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: #50E3C2;
          color: #50E3C2;
          transform: translateY(-50%) scale(1.1);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 2rem;
        }

        .modal-content {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          max-width: 450px;
          width: 100%;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        }

        .modal-content h2 {
          margin: 0 0 0.5rem 0;
          color: #2A2A2A;
        }

        .btn-secondary {
          background: #f5f5f5;
          color: #2A2A2A;
        }

        .btn-secondary:hover {
          background: #e0e0e0;
        }
      `}</style>
    </div>
  );
};

export default Login;
